import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Flex,
  SimpleGrid,
  Text,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { FaEdit, FaFilePdf } from 'react-icons/fa';
import PageLoader from 'components/wholePageLoader/PageLoader';
import { ViewAdminUserData } from 'reactRedux/thunks/adminUserThunk/adminUserThunk';
import {
  loginUserRole,
  revenueOption,
  sideBarRoutes,
  YearOptionsForInvoice,
  QuarterOptionsForInvoice,
  monthsSelectionsOptions,
  selectDaysOptions,
  languageOptions,
  regionLanguageConfig,
} from 'utils/StaticData';
import RoleBasedMerchantList from './RoleBasedMerchantList';
import BackButton from 'components/backButton/BackButton';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { generateInvoiceValidationSchema } from 'utils/CommonValidation';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import { DownloadSellerInvoicePDF } from 'reactRedux/thunks/settlementsThunk/SettlementsThunk';

const AdminUserDetail = () => {
  const { adminUserDetailId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { adminDetails } = useSelector((state) => state.authSlice);
  const { isViewLoading, viewAdminUserDetails } = useSelector(
    (state) => state.AdminUserSlice,
  );
  const { isPDFDownload } = useSelector((state) => state.SettlementsSlice);

  // Invoice modal state
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isSuccess, setIsSuccess] = useState(false);
  const currentYear = new Date().getFullYear();

  const defaultYearOption = YearOptionsForInvoice.find(
    (option) => option.label === currentYear,
  );

  const getDefaultLanguage = (regionId) => {
    const config = regionLanguageConfig.find((c) => c.regionId === regionId);
    const languageId = config ? config.languageId : 'en';
    return languageOptions.find((opt) => opt.id === languageId);
  };

  const {
    control,
    handleSubmit,
    watch,
    reset,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(generateInvoiceValidationSchema),
    defaultValues: {
      year: defaultYearOption || null,
      daysSelect: {
        id: 1,
        label: 'Quarters',
      },
      quarter: null,
      month: null,
      language: { id: 'en', label: 'English' },
    },
  });
  useEffect(() => {
    if (adminUserDetailId) {
      dispatch(ViewAdminUserData({ id: adminUserDetailId }));
    }
  }, [adminUserDetailId, dispatch]);
  useEffect(() => {
    if (isOpen) {
      setValue(
        'language',
        getDefaultLanguage(viewAdminUserDetails?.region?.regionId),
      );
    }
  }, [isOpen, setValue, viewAdminUserDetails]);
  // Handle success state
  useEffect(() => {
    if (isSuccess && !isPDFDownload) {
      onClose();
      reset();
      setIsSuccess(false);
    }
  }, [isPDFDownload, isSuccess, onClose, reset]);

  const handleGenerate = () => {
    const selectedDays = watch('daysSelect')?.id;
    const payload = {
      sellerId: adminUserDetailId,
      year: watch('year')?.label,
      ...(selectedDays === 1 && { quarter: watch('quarter')?.id }),
      ...(selectedDays === 2 && { month: watch('month')?.id }),
      language: watch('language')?.id,
      name: `${viewAdminUserDetails?.firstName} ${viewAdminUserDetails?.lastName}`,
    };

    dispatch(
      DownloadSellerInvoicePDF(
        payload,
        () => setIsSuccess(true),
        () => {
          onClose();
          reset();
        },
      ),
    );
  };
  const regionData = viewAdminUserDetails?.region;
  return (
    <>
      {isViewLoading ? (
        <PageLoader />
      ) : (
        <>
          <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
            <Flex justifyContent="space-between" mb={4} gap="3">
              <BackButton
                fallbackUrl="/admin/admin-users"
                label="Back to Admin Users"
              />
              <Flex gap="3">
                {adminDetails &&
                  adminDetails?.role !== sideBarRoutes?.Merchant &&
                  (viewAdminUserDetails?.role === sideBarRoutes?.Seller ||
                    viewAdminUserDetails?.role === sideBarRoutes?.Dealer) && (
                    <Button
                      py="10px"
                      px="15px"
                      fontWeight="semibold"
                      borderRadius="8px"
                      bg="primary"
                      color="white"
                      _hover={{ bg: 'primary', opacity: 0.8 }}
                      type="button"
                      leftIcon={<FaFilePdf size={25} />}
                      onClick={() => onOpen()}
                    >
                      Generate Invoice
                    </Button>
                  )}
                {adminDetails &&
                  adminDetails?.role !== sideBarRoutes?.Merchant && (
                    <Button
                      py="10px"
                      px="15px"
                      fontWeight="semibold"
                      borderRadius="8px"
                      bg="primary"
                      color="white"
                      _hover={{ bg: 'primary', opacity: 0.8 }}
                      type="button"
                      leftIcon={<FaEdit size={22} />}
                      onClick={() =>
                        navigate(
                          `/admin/admin-users/edit/${viewAdminUserDetails?.id}`,
                        )
                      }
                    >
                      Edit User
                    </Button>
                  )}
              </Flex>
            </Flex>
            <Flex direction="column" align="center" justify="center">
              {viewAdminUserDetails ? (
                <>
                  <Card
                    w="100%"
                    boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                    borderRadius="20px"
                    paddingInline="40px"
                    paddingBlock="40px"
                    overflowX={{ sm: 'scroll', lg: 'hidden' }}
                  >
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <Flex
                        alignItems={{ sm: 'start', md: 'center' }}
                        direction={{ sm: 'column', md: 'row' }}
                        gap="10px"
                        padding="4px 8px"
                        borderLeft={'1px solid #ccc'}
                      >
                        <Text fontWeight="normal">First Name:</Text>
                        <Text fontWeight="semibold">
                          {viewAdminUserDetails?.firstName}
                        </Text>
                      </Flex>
                      <Flex
                        alignItems={{ sm: 'start', md: 'center' }}
                        direction={{ sm: 'column', md: 'row' }}
                        gap="10px"
                        padding="4px 8px"
                        borderLeft={'1px solid #ccc'}
                      >
                        <Text fontWeight="normal">Last Name:</Text>
                        <Text fontWeight="semibold">
                          {viewAdminUserDetails?.lastName || 'N/A'}
                        </Text>
                      </Flex>
                      {viewAdminUserDetails.email && (
                        <Flex
                          alignItems={{ sm: 'start', md: 'center' }}
                          direction={{ sm: 'column', md: 'row' }}
                          gap="10px"
                          padding="4px 8px"
                          borderLeft={'1px solid #ccc'}
                        >
                          <Text fontWeight="normal">Email:</Text>
                          <Text fontWeight="semibold">
                            {viewAdminUserDetails?.email}
                          </Text>
                        </Flex>
                      )}
                      <Flex
                        alignItems={{ sm: 'start', md: 'center' }}
                        direction={{ sm: 'column', md: 'row' }}
                        gap="10px"
                        padding="4px 8px"
                        borderLeft={'1px solid #ccc'}
                      >
                        <Text fontWeight="normal">Role:</Text>
                        <Text fontWeight="semibold">
                          {loginUserRole[viewAdminUserDetails?.role] || 'N/A'}
                        </Text>
                      </Flex>
                      {viewAdminUserDetails?.stripeConnectAccountId && (
                        <Flex
                          alignItems={{ sm: 'start', md: 'center' }}
                          direction={{ sm: 'column', md: 'row' }}
                          gap="10px"
                          padding="4px 8px"
                          borderLeft={'1px solid #ccc'}
                        >
                          <Text fontWeight="normal">
                            Stripe Connect Account ID:
                          </Text>
                          <Text fontWeight="semibold">
                            {viewAdminUserDetails?.connectedAccount
                              ?.stripeAccountId ||
                              viewAdminUserDetails?.stripeConnectAccountId}
                          </Text>
                        </Flex>
                      )}
                      {viewAdminUserDetails?.role === sideBarRoutes?.Seller && (
                        <>
                          <Flex
                            alignItems={{ sm: 'start', md: 'center' }}
                            direction={{ sm: 'column', md: 'row' }}
                            gap="10px"
                            padding="4px 8px"
                            borderLeft={'1px solid #ccc'}
                          >
                            <Text fontWeight="normal">Commission On:</Text>
                            <Text fontWeight="semibold">
                              {revenueOption.find(
                                (revenue) =>
                                  revenue.id ===
                                  viewAdminUserDetails?.commissionOn,
                              )?.label || 'N/A'}
                            </Text>
                          </Flex>
                          <Flex
                            alignItems={{ sm: 'start', md: 'center' }}
                            direction={{ sm: 'column', md: 'row' }}
                            gap="10px"
                            padding="4px 8px"
                            borderLeft={'1px solid #ccc'}
                          >
                            <Text fontWeight="normal">Commission:</Text>
                            <Text fontWeight="semibold">
                              {viewAdminUserDetails?.commission !== null &&
                              viewAdminUserDetails?.commission !== undefined
                                ? `${viewAdminUserDetails.commission}%`
                                : 'N/A'}
                            </Text>
                          </Flex>
                          <Flex
                            alignItems={{ sm: 'start', md: 'center' }}
                            direction={{ sm: 'column', md: 'row' }}
                            gap="10px"
                            padding="4px 8px"
                            borderLeft={'1px solid #ccc'}
                          >
                            <Text fontWeight="normal">Commission Cap:</Text>
                            <Text fontWeight="semibold">
                              {viewAdminUserDetails?.commissionCap
                                ? `${viewAdminUserDetails?.commissionCap}`
                                : 'N/A'}
                            </Text>
                          </Flex>
                          <Flex
                            alignItems={{ sm: 'start', md: 'center' }}
                            direction={{ sm: 'column', md: 'row' }}
                            gap="10px"
                            padding="4px 8px"
                            borderLeft={'1px solid #ccc'}
                          >
                            <Text fontWeight="normal">Bank Account:</Text>
                            <Text fontWeight="semibold">
                              {viewAdminUserDetails?.bankAccount || 'N/A'}
                            </Text>
                          </Flex>
                          <Flex
                            alignItems={{ sm: 'start', md: 'center' }}
                            direction={{ sm: 'column', md: 'row' }}
                            gap="10px"
                            padding="4px 8px"
                            borderLeft={'1px solid #ccc'}
                          >
                            <Text fontWeight="normal">Address Line 1:</Text>
                            <Text fontWeight="semibold">
                              {viewAdminUserDetails?.addressLine1 || 'N/A'}
                            </Text>
                          </Flex>
                          <Flex
                            alignItems={{ sm: 'start', md: 'center' }}
                            direction={{ sm: 'column', md: 'row' }}
                            gap="10px"
                            padding="4px 8px"
                            borderLeft={'1px solid #ccc'}
                          >
                            <Text fontWeight="normal">Address Line 2:</Text>
                            <Text fontWeight="semibold">
                              {viewAdminUserDetails?.addressLine2 || 'N/A'}
                            </Text>
                          </Flex>
                          <Flex
                            alignItems={{ sm: 'start', md: 'center' }}
                            direction={{ sm: 'column', md: 'row' }}
                            gap="10px"
                            padding="4px 8px"
                            borderLeft={'1px solid #ccc'}
                          >
                            <Text fontWeight="normal">Zip Code:</Text>
                            <Text fontWeight="semibold">
                              {viewAdminUserDetails?.zipCode || 'N/A'}
                            </Text>
                          </Flex>
                          <Flex
                            alignItems={{ sm: 'start', md: 'center' }}
                            direction={{ sm: 'column', md: 'row' }}
                            gap="10px"
                            padding="4px 8px"
                            borderLeft={'1px solid #ccc'}
                          >
                            <Text fontWeight="normal">City:</Text>
                            <Text fontWeight="semibold">
                              {viewAdminUserDetails?.city || 'N/A'}
                            </Text>
                          </Flex>
                          <Flex
                            alignItems={{ sm: 'start', md: 'center' }}
                            direction={{ sm: 'column', md: 'row' }}
                            gap="10px"
                            padding="4px 8px"
                            borderLeft={'1px solid #ccc'}
                          >
                            <Text fontWeight="normal">Province:</Text>
                            <Text fontWeight="semibold">
                              {viewAdminUserDetails?.province || 'N/A'}
                            </Text>
                          </Flex>
                          <Flex
                            alignItems={{ sm: 'start', md: 'center' }}
                            direction={{ sm: 'column', md: 'row' }}
                            gap="10px"
                            padding="4px 8px"
                            borderLeft={'1px solid #ccc'}
                          >
                            <Text fontWeight="normal">Country:</Text>
                            <Text fontWeight="semibold">
                              {viewAdminUserDetails?.country || 'N/A'}
                            </Text>
                          </Flex>
                        </>
                      )}
                    </SimpleGrid>
                  </Card>
                </>
              ) : (
                <Text>No admin user details found.</Text>
              )}
            </Flex>
          </Box>
          {viewAdminUserDetails?.merchants?.length > 0 &&
            (viewAdminUserDetails.role === sideBarRoutes?.Merchant ||
              viewAdminUserDetails?.role === sideBarRoutes?.Seller) && (
              <>
                <Box pt="30px" alignItems="left">
                  <Text fontSize="2xl" fontWeight="medium">
                    Merchant List
                  </Text>
                </Box>
                <Card
                  w="100%"
                  boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                  borderRadius="20px"
                  paddingInline="40px"
                  paddingBlock="40px"
                  overflowX={{ sm: 'scroll', lg: 'hidden' }}
                  mt="20px"
                >
                  <RoleBasedMerchantList
                    tableData={viewAdminUserDetails?.merchants}
                    isLoading={isViewLoading}
                    role={viewAdminUserDetails?.role}
                  />
                </Card>
              </>
            )}
          {viewAdminUserDetails?.region &&
            viewAdminUserDetails?.role === sideBarRoutes?.Dealer && (
              <>
                <Box pt="30px" alignItems="left">
                  <Text fontSize="2xl" fontWeight="medium">
                    Region Details
                  </Text>
                </Box>
                <Card
                  w="100%"
                  boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                  borderRadius="20px"
                  paddingInline="40px"
                  paddingBlock="40px"
                  overflowX={{ sm: 'scroll', lg: 'hidden' }}
                  mt="20px"
                >
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="10px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontWeight="normal">Region Name:</Text>
                      <Text fontWeight="semibold">
                        {regionData?.regionName || 'N/A'}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="10px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontWeight="normal">Tax:</Text>
                      <Text fontWeight="semibold">
                        {regionData?.tax || 'N/A'}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="10px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontWeight="normal">Lost Charge:</Text>
                      <Text fontWeight="semibold">
                        {regionData?.lostCharge || 'N/A'}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="10px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontWeight="normal">Profit Share:</Text>
                      <Text fontWeight="semibold">
                        {regionData?.profitShare || 'N/A'}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="10px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontWeight="normal">Settlement Days:</Text>
                      <Text fontWeight="semibold">
                        {regionData?.settlementDays || 'N/A'}
                      </Text>
                    </Flex>
                  </SimpleGrid>
                  <SimpleGrid columns={{ base: 1, md: 1 }} mt="10px">
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="10px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontWeight="normal">Address:</Text>
                      <Text fontWeight="semibold">
                        {regionData?.address || 'N/A'}
                      </Text>
                    </Flex>
                  </SimpleGrid>
                </Card>
              </>
            )}
          {viewAdminUserDetails?.role === sideBarRoutes?.Dealer &&
            viewAdminUserDetails?.stripeConnectAccountId && (
              <>
                <Box pt="30px" alignItems="left">
                  <Text fontSize="2xl" fontWeight="medium">
                    Stripe Account Details
                  </Text>
                </Box>
                <Card
                  w="100%"
                  boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                  borderRadius="20px"
                  paddingInline="40px"
                  paddingBlock="40px"
                  overflowX={{ sm: 'scroll', lg: 'hidden' }}
                  mt="20px"
                >
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="10px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontWeight="normal">Account Name:</Text>
                      <Text fontWeight="semibold">
                        {viewAdminUserDetails?.connectedAccount?.name || 'N/A'}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="10px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontWeight="normal">Stripe ID:</Text>
                      <Text fontWeight="semibold">
                        {viewAdminUserDetails?.connectedAccount
                          ?.stripeAccountId || 'N/A'}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="10px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontWeight="normal">Country:</Text>
                      <Text fontWeight="semibold">
                        {viewAdminUserDetails?.connectedAccount?.country ||
                          'N/A'}
                      </Text>
                    </Flex>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="10px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontWeight="normal">Status:</Text>
                      <Text fontWeight="semibold">
                        {viewAdminUserDetails?.isConnectedAccountActive
                          ? 'Active'
                          : 'Inactive'}
                      </Text>
                    </Flex>
                  </SimpleGrid>
                </Card>
              </>
            )}
        </>
      )}

      {/* Modal for generating invoice */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        closeOnOverlayClick={false}
        size={{ base: 'sm', md: 'md', lg: 'lg' }}
      >
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSubmit(handleGenerate)} noValidate>
            <ModalHeader>Generate Invoice</ModalHeader>
            <ModalCloseButton
              onClick={() => {
                reset();
                clearErrors();
              }}
            />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Controller
                  name="year"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CommonReactSelect
                      name="year"
                      type="select"
                      options={YearOptionsForInvoice}
                      value={value ? [value] : []}
                      onChange={onChange}
                      register={control}
                      error={errors?.year}
                      getOptionLabel={(e) => e?.label}
                      getOptionValue={(e) => e?.id}
                      placeholderValue="Year"
                    />
                  )}
                />

                <Controller
                  name="daysSelect"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CommonReactSelect
                      name="daysSelect"
                      type="select"
                      options={selectDaysOptions}
                      value={value ? [value] : []}
                      onChange={(selected) => {
                        onChange(selected);
                        if (selected?.id === 2) {
                          setValue('quarter', null);
                          clearErrors('quarter');
                        } else if (selected?.id === 1) {
                          setValue('month', null);
                          clearErrors('month');
                        }
                      }}
                      register={control}
                      error={errors?.year}
                      getOptionLabel={(e) => e?.label}
                      getOptionValue={(e) => e?.id}
                      placeholderValue="Days"
                    />
                  )}
                />

                {watch('daysSelect')?.id === 1 ? (
                  <>
                    <Controller
                      name="quarter"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CommonReactSelect
                          label="Quarter"
                          name="quarter"
                          type="select"
                          options={QuarterOptionsForInvoice}
                          value={value ? [value] : []}
                          onChange={onChange}
                          register={control}
                          isRequired={true}
                          error={errors?.quarter}
                          getOptionLabel={(e) => e?.label}
                          getOptionValue={(e) => e?.id}
                        />
                      )}
                    />
                  </>
                ) : (
                  <>
                    <Controller
                      name="month"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CommonReactSelect
                          label="Months"
                          name="month"
                          type="select"
                          options={monthsSelectionsOptions}
                          value={value ? [value] : []}
                          onChange={onChange}
                          register={control}
                          isRequired={true}
                          error={errors?.month}
                          getOptionLabel={(e) => e?.label}
                          getOptionValue={(e) => e?.id}
                        />
                      )}
                    />
                  </>
                )}

                <Controller
                  name="language"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CommonReactSelect
                      label="Language"
                      name="language"
                      type="select"
                      options={languageOptions}
                      value={value ? [value] : []}
                      onChange={onChange}
                      register={control}
                      error={errors?.language}
                      getOptionLabel={(e) => e?.label}
                      getOptionValue={(e) => e?.id}
                      placeholderValue="Language"
                    />
                  )}
                />
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                type="button"
                bg="primary"
                color="white"
                _hover={{ bg: 'primary', opacity: 0.8 }}
                mr={3}
                disabled={isPDFDownload}
                isLoading={isPDFDownload}
                loadingText="Generating..."
                fontSize="sm"
                fontWeight="500"
                onClick={handleSubmit(handleGenerate)}
              >
                Download Invoice
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AdminUserDetail;
